/**
 * "Monatsübersicht & Forecast" — the six-month money window (Slice 18).
 *
 * WHAT THIS CARD IS NOT. The design reference draws occupied/free room bars and
 * an occupancy line across six months. Vantio has no such contract: there is no
 * reserved-room-day aggregation, no future occupied/free room count, no
 * forecast occupancy rate. Inventing one — extrapolating today's occupancy,
 * deriving free as inventory minus occupied, or copying the mockup's 75 % — is
 * exactly the class of placeholder Slice 12A removed from this page. So the
 * reference is taken as the CARD'S FORM, not as its semantics.
 *
 * WHAT IT SHOWS INSTEAD, all of it already loaded and already visible on this
 * page before the redesign: the six `/api/admin/profit` windows the page has
 * always fetched (two months back, the selected month, three forward), summed
 * over the filtered population by `sumFilteredProfitField`. Umsatz as bars,
 * Gewinn as a line. Both are CHF on one axis, from one response per month, over
 * one population — no semantic mix. The selected month's three totals sit above
 * the chart, and the two Slice-9B averages below it.
 *
 * `null` is not zero. A month whose window failed or has not arrived draws no
 * bar and no point, and the tooltip says so, rather than sitting on the axis
 * looking like a month that earned nothing.
 *
 * Label → value → hint as adjacent siblings throughout; see CoLivingKpiCard.
 */
import React from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Chart marks need concrete paint — an SVG `fill` cannot read a Tailwind class.
 * Grid and axes below DO use `currentColor` plus a token class, so everything
 * that can follow the theme does. Same split as `RevenueReceivablesCard`.
 */
const SERIES = {
  umsatz: "#10b981",
  gewinn: "#0ea5e9",
};

/**
 * The selected month's bar, one step darker than its five neighbours.
 *
 * WHY THIS EXISTS AT ALL. The table this card replaced marked the selected
 * month with a `●` in its month cell, and six months drawn identically answer
 * "how did the year go" but not "which one am I looking at" — the question the
 * filter above the page just asked. Losing that was a regression of the
 * redesign, not a simplification, so the distinction is back: as paint here,
 * as the same `●` in the text equivalent, and as a sentence in its caption so
 * a screen reader gets it as language rather than as a bullet.
 */
const SELECTED_UMSATZ = "#047857";

function MoneyStat({ label, value, hint }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="m-0 truncate text-[10px] font-medium text-muted-foreground">{label}</p>
      <p className="m-0 text-[16px] font-semibold leading-tight text-foreground">{value}</p>
      <p className="m-0 truncate text-[10px] text-muted-foreground">{hint}</p>
    </div>
  );
}

function ChartTooltip({ active, payload, label, formatCurrency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-[8px] border border-border bg-popover px-2.5 py-2 text-[11px] text-popover-foreground shadow-sm">
      <p className="m-0 mb-1 font-medium">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="m-0 flex items-center gap-2">
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: p.color }}
            aria-hidden
          />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-mono font-medium">
            {p.value === null || p.value === undefined
              ? "keine Daten"
              : formatCurrency(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * @param {Array<{month,umsatz,gewinn}>} series  six months, nulls allowed
 * @param {function} formatCurrency  the page's single CHF formatter
 * @param {function} formatChfOrDash the page's null-safe CHF formatter
 */
export default function CoLivingMonthlyForecast({
  series,
  formatCurrency,
  formatChfOrDash,
  monthRevenue,
  monthProfit,
  monthCosts,
  criticalUnits,
  averageRevenuePerRoom,
  averageProfitPerUnit,
}) {
  const compactTick = (v) =>
    Math.abs(Number(v)) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : `${v}`;

  const selectedLabel = series.find((row) => row.isSelected)?.month ?? "—";

  return (
    <section
      aria-label="Monatsübersicht & Forecast"
      className="flex flex-col rounded-[10px] border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="m-0 text-[13px] font-semibold text-foreground">
            Monatsübersicht &amp; Forecast
          </h2>
          <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
            Umsatz und Gewinn der sechs Monate um den gewählten Monat
          </p>
        </div>
        <ul className="m-0 flex list-none items-center gap-3 p-0">
          {[
            { k: "umsatz", name: "Umsatz" },
            { k: "gewinn", name: "Gewinn" },
          ].map((s) => (
            <li key={s.k} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span
                className="h-2 w-2 rounded-full"
                style={{ background: SERIES[s.k] }}
                aria-hidden
              />
              {s.name}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-3 flex flex-wrap gap-3 border-y border-border py-3">
        <MoneyStat
          label="Umsatz (Monat)"
          value={formatChfOrDash(monthRevenue)}
          hint="Ist-Umsatz des gewählten Monats (Backend)"
        />
        <MoneyStat
          label="Gewinn (Monat)"
          value={formatChfOrDash(monthProfit)}
          hint="Umsatz minus Kosten des gewählten Monats"
        />
        <MoneyStat
          label="Kosten (Monat)"
          value={formatChfOrDash(monthCosts)}
          hint="Laufende Kosten des gewählten Monats"
        />
        {/* Kept from the pre-redesign hero row, moved to the card whose subject
            it shares. It is the SAME contract as before — units in the selected
            month's profit window whose `profit < 0` — with no new threshold and
            no second condition. Null when the window has not arrived, because a
            literal 0 would read as "no unit is critical" on a dead request. */}
        <MoneyStat
          label="Kritische Units"
          value={criticalUnits === null || criticalUnits === undefined ? "—" : criticalUnits}
          hint="Negativer Gewinn im gewählten Monat"
        />
      </div>

      <div className="mt-3 h-[224px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -14 }}>
            <CartesianGrid
              stroke="currentColor"
              className="text-border"
              strokeDasharray="3 4"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              interval={0}
              tick={{ fontSize: 10, fill: "currentColor" }}
              className="text-muted-foreground"
            />
            <YAxis
              tickFormatter={compactTick}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10, fill: "currentColor" }}
              className="text-muted-foreground"
            />
            <Tooltip
              cursor={false}
              content={<ChartTooltip formatCurrency={formatCurrency} />}
            />
            <Bar
              dataKey="umsatz"
              name="Umsatz"
              fill={SERIES.umsatz}
              radius={[4, 4, 0, 0]}
              maxBarSize={38}
              isAnimationActive={false}
            >
              {series.map((row) => (
                <Cell
                  key={row.month}
                  fill={row.isSelected ? SELECTED_UMSATZ : SERIES.umsatz}
                />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="gewinn"
              name="Gewinn"
              stroke={SERIES.gewinn}
              strokeWidth={1.8}
              dot={{ r: 2.5, fill: SERIES.gewinn }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* The chart's data as text. Same pattern as RevenueReceivablesCard: a
          canvas is not readable, a table is. */}
      <table className="sr-only">
        <caption>
          {`Umsatz und Gewinn je Monat. Gewählter Monat: ${selectedLabel}.`}
        </caption>
        <thead>
          <tr>
            <th scope="col">Monat</th>
            <th scope="col">Umsatz</th>
            <th scope="col">Gewinn</th>
          </tr>
        </thead>
        <tbody>
          {series.map((row) => (
            <tr key={row.month}>
              {/* The `●` is the marker the retired table used, kept so the
                  selected row is identifiable in the text equivalent too. The
                  caption above says the same thing in words, which is what a
                  screen reader actually needs — a bullet read aloud is not an
                  answer. */}
              <th scope="row">{row.isSelected ? `${row.month} ●` : row.month}</th>
              <td>{formatChfOrDash(row.umsatz)}</td>
              <td>{formatChfOrDash(row.gewinn)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap gap-3 border-t border-border pt-3">
        <MoneyStat
          label="Ø Umsatz pro Room"
          value={formatChfOrDash(averageRevenuePerRoom)}
          hint="Ausgewählter Monat · je operativem Zimmer"
        />
        <MoneyStat
          label="Ø Gewinn pro Unit"
          value={formatChfOrDash(averageProfitPerUnit)}
          hint="Aktueller Durchschnitt über alle Co-Living Units"
        />
      </div>
    </section>
  );
}
